/** The one place that talks HTTP. Every call goes to the same origin with the
 * browser's own session cookie; a refusal becomes an `ApiError` carrying the
 * backend's own sentence, so screens show what the server said rather than a
 * status code. */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }

  get unauthenticated(): boolean {
    return this.status === 401;
  }

  get forbidden(): boolean {
    return this.status === 403;
  }
}

async function refusal(response: Response): Promise<ApiError> {
  let detail = `${response.status} ${response.statusText}`.trim();
  try {
    const body: unknown = await response.json();
    if (typeof body === "object" && body !== null && "detail" in body) {
      const found = (body as { detail: unknown }).detail;
      detail = typeof found === "string" ? found : JSON.stringify(found);
    }
  } catch {
    // Not JSON; the status line is what there is.
  }
  return new ApiError(detail, response.status);
}

const JSON_HEADERS = { Accept: "application/json" };

async function send<T>(url: string, init: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: "same-origin",
    ...init,
    headers: { ...JSON_HEADERS, ...(init.headers ?? {}) },
  });
  if (!response.ok) {
    throw await refusal(response);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export function get<T>(url: string): Promise<T> {
  return send<T>(url, { method: "GET" });
}

export function post<T>(url: string, body?: unknown): Promise<T> {
  return send<T>(url, {
    method: "POST",
    headers: body === undefined ? {} : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export function put<T>(url: string, body: unknown): Promise<T> {
  return send<T>(url, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function patch<T>(url: string, body: unknown): Promise<T> {
  return send<T>(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function del<T>(url: string): Promise<T> {
  return send<T>(url, { method: "DELETE" });
}

/** A file upload: multipart, so no JSON content type. */
export function postForm<T>(url: string, form: FormData): Promise<T> {
  return send<T>(url, { method: "POST", body: form });
}

/** A URL with a query string, skipping empty values. */
export function withQuery(url: string, params: Record<string, string | number | null | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== "") {
      query.set(key, String(value));
    }
  }
  const text = query.toString();
  return text === "" ? url : `${url}?${text}`;
}

/** A document the server serves, downloaded as a file rather than opened. */
export function downloadFile(href: string): void {
  const link = document.createElement("a");
  link.href = href;
  link.download = "";
  document.body.append(link);
  link.click();
  link.remove();
}
